import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { clickable } from './utils';

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

// ─── Icons ────────────────────────────────────────────────────────────────────
function IconCopy() {
  return <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>;
}
function IconCheck() {
  return <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>;
}
function IconRefresh({ spinning }) {
  return <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={spinning ? { animation: 'spin 1s linear infinite' } : {}}><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>;
}
function IconSettings() {
  return <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>;
}

// Microsoft brand icon
function IconMicrosoft() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" fill="none">
      <rect x="1"  y="1"  width="9" height="9" fill="#F25022"/>
      <rect x="11" y="1"  width="9" height="9" fill="#7FBA00"/>
      <rect x="1"  y="11" width="9" height="9" fill="#00A4EF"/>
      <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
    </svg>
  );
}

// ─── CopyButton ───────────────────────────────────────────────────────────────
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button onClick={handleCopy} title={copied ? 'Copied!' : 'Copy System ID'}
      style={{ padding: '3px 8px', borderRadius: 5, border: `1px solid ${copied ? '#86efac' : 'var(--border)'}`, background: copied ? '#f0fdf4' : 'var(--surface-2)', cursor: 'pointer', color: copied ? '#16a34a' : 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, fontFamily: 'DM Sans', transition: 'all .15s', flexShrink: 0 }}>
      {copied ? <IconCheck /> : <IconCopy />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

// ─── SearchableSelect ─────────────────────────────────────────────────────────
function SearchableSelect({ options, value, onChange, placeholder }) {
  const [query, setQuery]   = useState('');
  const [open, setOpen]     = useState(false);
  const containerRef        = useRef(null);

  // Close on outside click
  useEffect(() => {
    const h = e => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const displayed = open ? query : (value || '');
  const matches   = useMemo(() => {
    const q = query.toLowerCase();
    return q ? options.filter(o => o.toLowerCase().includes(q)) : options;
  }, [options, query]);

  const select = opt => { onChange(opt); setQuery(''); setOpen(false); };
  const clear  = e  => { e.stopPropagation(); onChange(''); setQuery(''); setOpen(false); };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', border: `1px solid ${open ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 7, background: 'var(--surface-2)', transition: 'border-color .15s' }}>
        <input
          type="text"
          value={displayed}
          onChange={e => { setQuery(e.target.value); setOpen(true); if (!e.target.value) onChange(''); }}
          onFocus={() => { setOpen(true); setQuery(''); }}
          placeholder={placeholder}
          style={{ flex: 1, padding: '8px 10px', border: 'none', background: 'transparent', outline: 'none', fontSize: 13, fontFamily: 'DM Sans', color: value && !open ? 'var(--text)' : 'var(--text)', minWidth: 0 }}
        />
        {value
          ? <button onClick={clear} style={{ padding: '0 10px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-2)', fontSize: 16, lineHeight: 1, flexShrink: 0 }}>×</button>
          : <span style={{ padding: '0 10px', color: 'var(--text-2)', fontSize: 12, flexShrink: 0 }}>▾</span>
        }
      </div>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 3px)', left: 0, right: 0, zIndex: 200, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 6px 24px rgba(0,0,0,.12)', maxHeight: 260, overflowY: 'auto' }}>
          {matches.length === 0
            ? <div style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text-2)', fontStyle: 'italic' }}>No matches</div>
            : matches.slice(0, 100).map(opt => (
                <div key={opt} onMouseDown={() => select(opt)}
                  style={{ padding: '8px 14px', cursor: 'pointer', fontSize: 13, background: opt === value ? 'var(--accent-light)' : 'transparent', color: opt === value ? 'var(--accent)' : 'var(--text)', fontWeight: opt === value ? 600 : 400, transition: 'background .1s' }}
                  onMouseEnter={e => { if (opt !== value) e.currentTarget.style.background = 'var(--surface-2)'; }}
                  onMouseLeave={e => { if (opt !== value) e.currentTarget.style.background = 'transparent'; }}>
                  {opt}
                </div>
              ))
          }
          {matches.length > 100 && <div style={{ padding: '6px 14px', fontSize: 11, color: 'var(--text-2)' }}>+{matches.length - 100} more — keep typing to narrow down</div>}
        </div>
      )}
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

// ─── Step indicator ───────────────────────────────────────────────────────────
function Steps({ current, labels }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 28 }}>
      {labels.map((label, i) => {
        const s = i + 1;
        const done = current > s;
        const active = current === s;
        return (
          <React.Fragment key={s}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, background: active ? 'var(--accent)' : done ? '#16a34a' : 'var(--surface-2)', color: (active || done) ? '#fff' : 'var(--text-2)', border: (active || done) ? 'none' : '1px solid var(--border)' }}>
                {done ? '✓' : s}
              </div>
              <span style={{ fontSize: 13, fontWeight: active ? 600 : 400, color: active ? 'var(--text)' : 'var(--text-2)' }}>{label}</span>
            </div>
            {i < labels.length - 1 && <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── SetupWizard ──────────────────────────────────────────────────────────────
function SetupWizard({ config, onSaved, initialError }) {
  // step 1 = credentials+sign-in, step 2 = company picker
  const initialStep = config?.authenticated && !config?.company_id ? 2 : 1;
  const [step, setStep]             = useState(initialStep);
  const [form, setForm]             = useState({ bc_tenant_id: '', bc_client_id: '', bc_client_secret: '', bc_environment: 'production' });
  const [companies, setCompanies]   = useState([]);
  const [selectedCo, setSelectedCo] = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(initialError || null);

  // Auto-load companies if already authenticated and at step 2
  useEffect(() => {
    if (step === 2 && companies.length === 0) {
      setLoading(true);
      fetch(`${API}/api/bc/companies`)
        .then(r => { if (!r.ok) return r.json().then(e => Promise.reject(e.detail)); return r.json(); })
        .then(list => { setCompanies(list); if (list.length === 1) setSelectedCo(list[0]); })
        .catch(e => setError(String(e)))
        .finally(() => setLoading(false));
    }
  }, [step]);

  const saveAndSignIn = async () => {
    if (!form.bc_tenant_id || !form.bc_client_id) return;
    setError(null); setLoading(true);
    try {
      await fetch(`${API}/api/bc/config`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const r = await fetch(`${API}/api/bc/auth/login`);
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed');
      const { auth_url } = await r.json();
      window.location.href = auth_url;   // full-page redirect to Microsoft
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  };

  const saveCompany = async () => {
    if (!selectedCo) return;
    setError(null); setLoading(true);
    try {
      await fetch(`${API}/api/bc/config`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bc_company_id: selectedCo.id, bc_company_name: selectedCo.displayName || selectedCo.name }),
      });
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const field = (key, label, placeholder) => (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 5 }}>{label}</label>
      <input type="text" value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        placeholder={placeholder}
        style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, fontFamily: 'DM Sans', background: 'var(--surface-2)', outline: 'none', color: 'var(--text)' }} />
    </div>
  );

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 20px' }}>
      <div style={{ width: '100%', maxWidth: 520 }}>
        <Steps current={step} labels={['Entra ID & sign in', 'Select company']} />

        <div className="card">

          {/* ── Step 1 ── */}
          {step === 1 && (
            <>
              <div className="section-title" style={{ marginBottom: 4 }}>Connect to Business Central</div>

              {/* Delegated auth explanation */}
              <div style={{ marginBottom: 20, padding: '12px 14px', borderRadius: 8, background: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: 12, color: '#166534', lineHeight: 1.6 }}>
                <strong>Delegated login (recommended)</strong><br />
                You sign in with your own Microsoft account. Business Central enforces your personal BC permissions — no read/write scope needs to be granted at the Entra level beyond <code style={{ background: '#dcfce7', padding: '1px 4px', borderRadius: 3 }}>user_impersonation</code>. This dashboard only ever calls read-only API endpoints.
              </div>

              {/* Entra ID setup checklist */}
              <div style={{ marginBottom: 20, padding: '12px 14px', borderRadius: 8, background: 'var(--surface-2)', border: '1px solid var(--border)', fontSize: 12, color: 'var(--text-2)', lineHeight: 1.8 }}>
                <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>App registration checklist</div>
                <div>✅ <strong>API permission:</strong> Business Central API → <code style={{ fontSize: 11, background: 'var(--surface-2)', padding: '1px 4px', borderRadius: 3 }}>user_impersonation</code> (Delegated)</div>
                <div>✅ <strong>Redirect URI:</strong> <code style={{ fontSize: 11 }}>https://dashboard.dvlv.be/api/bc/auth/callback</code></div>
                <div>✅ <strong>In BC → Users:</strong> assign a read-only permission set to this app user</div>
              </div>

              {field('bc_tenant_id', 'Tenant ID', 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx')}
              {field('bc_client_id', 'Client ID (Application ID)', 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx')}

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 5 }}>
                  Client Secret
                </label>
                <input type="password" value={form.bc_client_secret}
                  onChange={e => setForm(f => ({ ...f, bc_client_secret: e.target.value }))}
                  placeholder={config?.client_secret_set ? '(saved — leave blank to keep)' : 'Your app registration secret value'}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, fontFamily: 'DM Sans', background: 'var(--surface-2)', outline: 'none', color: 'var(--text)' }} />
                <div style={{ marginTop: 5, fontSize: 11, color: 'var(--text-2)', lineHeight: 1.5 }}>
                  Used only to exchange the Microsoft auth code for a token — not for direct BC access. BC enforces your personal read-only permission set.
                </div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 5 }}>Environment</label>
                <select value={form.bc_environment} onChange={e => setForm(f => ({ ...f, bc_environment: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, fontFamily: 'DM Sans', background: 'var(--surface-2)', outline: 'none', color: 'var(--text)' }}>
                  <option value="production">production</option>
                  <option value="sandbox">sandbox</option>
                </select>
              </div>

              {error && <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 13 }}>{error}</div>}

              <button onClick={saveAndSignIn} disabled={loading || !form.bc_tenant_id || !form.bc_client_id}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '11px', background: loading ? 'var(--surface-2)' : '#2f2f2f', color: loading ? 'var(--text-2)' : '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, fontFamily: 'DM Sans', cursor: loading ? 'not-allowed' : 'pointer' }}>
                {!loading && <IconMicrosoft />}
                {loading ? 'Redirecting to Microsoft…' : 'Sign in with Microsoft'}
              </button>
            </>
          )}

          {/* ── Step 2 ── */}
          {step === 2 && (
            <>
              <div className="section-title" style={{ marginBottom: 4 }}>Select your company</div>
              {config?.signed_in_as && (
                <div style={{ marginBottom: 14, padding: '8px 12px', borderRadius: 8, background: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: 12, color: '#166534', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>✅</span> Signed in as <strong>{config.signed_in_as}</strong>
                </div>
              )}
              <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 16 }}>
                {loading ? 'Fetching companies…' : `${companies.length} compan${companies.length === 1 ? 'y' : 'ies'} found.`}
              </p>
              {loading && <div style={{ fontSize: 13, color: 'var(--text-2)' }}>Loading…</div>}
              {!loading && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                  {companies.map(co => {
                    const active = selectedCo?.id === co.id;
                    return (
                      <div key={co.id} {...clickable(() => setSelectedCo(co))} aria-pressed={active} aria-label={co.displayName || co.name}
                        style={{ padding: '12px 16px', borderRadius: 8, cursor: 'pointer', transition: 'all .15s', border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`, background: active ? 'var(--accent-light)' : 'var(--surface-2)' }}
                        onMouseEnter={e => { if (!active) e.currentTarget.style.borderColor = 'var(--accent)'; }}
                        onMouseLeave={e => { if (!active) e.currentTarget.style.borderColor = 'var(--border)'; }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: active ? 'var(--accent)' : 'var(--text)' }}>{co.displayName || co.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-2)', fontFamily: 'DM Mono', marginTop: 2 }}>{co.id}</div>
                      </div>
                    );
                  })}
                </div>
              )}
              {error && <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 13 }}>{error}</div>}
              <button onClick={saveCompany} disabled={!selectedCo || loading}
                style={{ width: '100%', padding: '10px', background: !selectedCo || loading ? 'var(--surface-2)' : 'var(--accent)', color: !selectedCo || loading ? 'var(--text-2)' : '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, fontFamily: 'DM Sans', cursor: !selectedCo || loading ? 'not-allowed' : 'pointer' }}>
                {loading ? 'Saving…' : 'Load job tasks →'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function BCProjectsPage() {
  const [config, setConfig]       = useState(null);
  const [showSetup, setShowSetup] = useState(false);
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);

  // Filters
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterJobNo, setFilterJobNo]       = useState('');
  const [filterJobName, setFilterJobName]   = useState('');
  const [filterTask, setFilterTask]         = useState('');

  // Sort
  const [sortCol, setSortCol] = useState('customer_name');
  const [sortDir, setSortDir] = useState('asc');

  // Check for OAuth error/success params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const bcError = params.get('bc_error');
    if (bcError) {
      setError(decodeURIComponent(bcError));
      // Clean URL
      window.history.replaceState({}, '', '/bc-projects');
    }
  }, []);

  const loadConfig = useCallback(() => {
    fetch(`${API}/api/bc/config`)
      .then(r => r.json())
      .then(d => setConfig(d))
      .catch(() => setConfig({ configured: false, authenticated: false }));
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const loadData = useCallback((forceRefresh = false) => {
    setLoading(true); setError(null);
    fetch(`${API}/api/bc/data${forceRefresh ? '?refresh=true' : ''}`)
      .then(r => { if (!r.ok) return r.json().then(e => Promise.reject(e.detail || 'Load failed')); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(String(e)); setLoading(false); });
  }, []);

  useEffect(() => {
    if (config?.configured && config?.authenticated && config?.company_id) loadData();
  }, [config, loadData]);

  const handleSignOut = () => {
    fetch(`${API}/api/bc/auth/logout`, { method: 'POST' })
      .then(() => { setData(null); loadConfig(); });
  };

  // Derived options
  const rows      = data?.rows || [];
  const customers = useMemo(() => [...new Set(rows.map(r => r.customer_name).filter(Boolean))].sort(), [rows]);

  const filtered  = useMemo(() => {
    let out = rows;
    if (filterCustomer) {
      const q = filterCustomer.toLowerCase();
      out = out.filter(r => r.customer_name.toLowerCase().includes(q));
    }
    if (filterJobNo) {
      const q = filterJobNo.toLowerCase();
      out = out.filter(r => r.job_number.toLowerCase().includes(q));
    }
    if (filterJobName) {
      const q = filterJobName.toLowerCase();
      out = out.filter(r => r.job_name.toLowerCase().includes(q));
    }
    if (filterTask) {
      const q = filterTask.toLowerCase();
      out = out.filter(r => r.task_number.toLowerCase().includes(q) || r.task_description.toLowerCase().includes(q));
    }
    return [...out].sort((a, b) => {
      const av = (a[sortCol] || '').toLowerCase(), bv = (b[sortCol] || '').toLowerCase();
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [rows, filterCustomer, filterJobNo, filterJobName, filterTask, sortCol, sortDir]);

  const clearFilters = () => { setFilterCustomer(''); setFilterJobNo(''); setFilterJobName(''); setFilterTask(''); };
  const hasFilters   = filterCustomer || filterJobNo || filterJobName || filterTask;
  const sortToggle   = col => { if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortCol(col); setSortDir('asc'); } };
  const SortIcon     = ({ col }) => sortCol !== col ? <span style={{ opacity: .25, fontSize: 10 }}>↕</span> : <span style={{ fontSize: 10, color: 'var(--accent)' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;

  // ── Not yet loaded ──────────────────────────────────────────────────────────
  if (config === null) return <div className="loading">Loading…</div>;

  // ── Setup / sign-in needed ──────────────────────────────────────────────────
  if (!config.configured || !config.authenticated || !config.company_id || showSetup) {
    return <SetupWizard config={config} initialError={error} onSaved={() => { setShowSetup(false); setError(null); loadConfig(); }} />;
  }

  // ── Main view ───────────────────────────────────────────────────────────────
  const thStyle = col => ({
    padding: '9px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700,
    color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.05em',
    borderBottom: '2px solid var(--border)', cursor: 'pointer', userSelect: 'none',
    background: sortCol === col ? 'var(--accent-light)' : 'transparent', whiteSpace: 'nowrap',
  });

  return (
    <div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
              {config.company_name || 'Business Central'} — Job Tasks
            </span>
            {data && <span style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'DM Mono' }}>{data.job_count} jobs · {data.task_count} tasks</span>}
          </div>
          {config.signed_in_as && (
            <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>
              Signed in as <strong>{config.signed_in_as}</strong> · read-only
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => loadData(true)} disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', border: '1px solid var(--border)', borderRadius: 7, background: 'var(--surface-2)', cursor: loading ? 'not-allowed' : 'pointer', fontSize: 13, fontFamily: 'DM Sans', color: 'var(--text-2)', fontWeight: 500 }}>
            <IconRefresh spinning={loading} />
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button onClick={handleSignOut}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', border: '1px solid var(--border)', borderRadius: 7, background: 'var(--surface-2)', cursor: 'pointer', fontSize: 13, fontFamily: 'DM Sans', color: 'var(--text-2)', fontWeight: 500 }}>
            Sign out
          </button>
          <button onClick={() => setShowSetup(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', border: '1px solid var(--border)', borderRadius: 7, background: 'var(--surface-2)', cursor: 'pointer', fontSize: 13, fontFamily: 'DM Sans', color: 'var(--text-2)', fontWeight: 500 }}>
            <IconSettings /> Settings
          </button>
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>⚠ {error}</span>
          {error.includes('expired') && (
            <button onClick={() => fetch(`${API}/api/bc/auth/login`).then(r => r.json()).then(d => { window.location.href = d.auth_url; })}
              style={{ padding: '5px 12px', border: 'none', borderRadius: 6, background: '#dc2626', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'DM Sans', display: 'flex', alignItems: 'center', gap: 6 }}>
              <IconMicrosoft /> Re-authenticate
            </button>
          )}
        </div>
      )}

      {/* ── Filter bar ── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>

          {/* Customer — searchable combobox */}
          <div style={{ minWidth: 200, flex: '2 1 200px' }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-2)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.05em' }}>
              Customer
            </label>
            <SearchableSelect
              options={customers}
              value={filterCustomer}
              onChange={v => { setFilterCustomer(v); }}
              placeholder={`All customers (${customers.length})`}
            />
          </div>

          {/* Job # — text search */}
          <div style={{ minWidth: 130, flex: '1 1 130px' }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-2)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.05em' }}>Job #</label>
            <input type="text" value={filterJobNo} onChange={e => setFilterJobNo(e.target.value)}
              placeholder="e.g. P00644"
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 7, fontSize: 13, fontFamily: 'DM Sans', background: 'var(--surface-2)', outline: 'none', color: 'var(--text)' }} />
          </div>

          {/* Job Name — text search */}
          <div style={{ minWidth: 180, flex: '2 1 180px' }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-2)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.05em' }}>Job Name</label>
            <input type="text" value={filterJobName} onChange={e => setFilterJobName(e.target.value)}
              placeholder="Search job name…"
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 7, fontSize: 13, fontFamily: 'DM Sans', background: 'var(--surface-2)', outline: 'none', color: 'var(--text)' }} />
          </div>

          {/* Task search */}
          <div style={{ minWidth: 180, flex: '2 1 180px' }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-2)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.05em' }}>Search tasks</label>
            <input type="text" value={filterTask} onChange={e => setFilterTask(e.target.value)}
              placeholder="Task # or description…"
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 7, fontSize: 13, fontFamily: 'DM Sans', background: 'var(--surface-2)', outline: 'none', color: 'var(--text)' }} />
          </div>

          {hasFilters && (
            <button onClick={clearFilters}
              style={{ padding: '8px 14px', border: '1px solid var(--border)', borderRadius: 7, background: 'var(--surface-2)', cursor: 'pointer', fontSize: 12, fontFamily: 'DM Sans', color: 'var(--text-2)', alignSelf: 'flex-end', whiteSpace: 'nowrap' }}>
              ✕ Clear all
            </button>
          )}
        </div>

        {data && (
          <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-2)' }}>
            Showing <strong style={{ color: 'var(--text)' }}>{filtered.length}</strong> of {rows.length} tasks
            {hasFilters && <span style={{ color: 'var(--accent)', marginLeft: 6 }}>(filtered)</span>}
          </div>
        )}
      </div>

      {/* ── States ── */}
      {loading && !data && <div className="loading">Fetching jobs and tasks from Business Central…</div>}
      {!loading && data && filtered.length === 0 && <div className="empty"><p>No tasks match the current filters.</p></div>}

      {/* ── Table ── */}
      {data && filtered.length > 0 && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--surface-2)' }}>
                  <th style={{ ...thStyle('customer_name'), minWidth: 140 }} onClick={() => sortToggle('customer_name')}>Customer <SortIcon col="customer_name" /></th>
                  <th style={thStyle('job_number')} onClick={() => sortToggle('job_number')}>Job # <SortIcon col="job_number" /></th>
                  <th style={{ ...thStyle('job_name'), minWidth: 180 }} onClick={() => sortToggle('job_name')}>Job Name <SortIcon col="job_name" /></th>
                  <th style={thStyle('task_number')} onClick={() => sortToggle('task_number')}>Task # <SortIcon col="task_number" /></th>
                  <th style={{ ...thStyle('task_description'), minWidth: 200 }} onClick={() => sortToggle('task_description')}>Description <SortIcon col="task_description" /></th>
                  <th style={{ padding: '9px 12px', fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap', minWidth: 210 }}>System ID</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, idx) => (
                  <tr key={row.task_id || idx} style={{ borderBottom: '1px solid var(--border)', transition: 'background .1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <td style={{ padding: '10px 12px', fontWeight: 500 }}>{row.customer_name || <span style={{ color: 'var(--text-2)', fontStyle: 'italic' }}>—</span>}</td>
                    <td style={{ padding: '10px 12px', fontFamily: 'DM Mono', fontSize: 12, color: 'var(--accent)', whiteSpace: 'nowrap' }}>{row.job_number}</td>
                    <td style={{ padding: '10px 12px' }}>{row.job_name || <span style={{ color: 'var(--text-2)', fontStyle: 'italic' }}>—</span>}</td>
                    <td style={{ padding: '10px 12px', fontFamily: 'DM Mono', fontSize: 12, whiteSpace: 'nowrap' }}>{row.task_number}</td>
                    <td style={{ padding: '10px 12px', maxWidth: 320 }}>{row.task_description || <span style={{ color: 'var(--text-2)', fontStyle: 'italic' }}>—</span>}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontFamily: 'DM Mono', fontSize: 11, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 150 }}>{row.task_id}</span>
                        <CopyButton text={row.task_id} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
